import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

interface Message {
  id: string;
  sender: string;
  senderRole: string;
  subject: string;
  preview: string;
  timestamp: string;
  isRead: boolean;
  priority?: 'high' | 'normal' | 'low';
}

interface User {
  role: 'admin' | 'teacher' | 'student' | 'parent';
}

interface CommunicationNotifierProps {
  user: User;
  messages?: Message[];
  onNavigate?: (path: string) => void;
  onMarkAsRead?: (messageId: string) => void;
}

const CommunicationNotifier = ({
  user,
  messages = [],
  onNavigate,
  onMarkAsRead,
}: CommunicationNotifierProps) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const count = messages.filter((msg) => !msg.isRead).length;
    setUnreadCount(count);
  }, [messages]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.notification-container')) setIsOpen(false);
    };

    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleNavigation = (path: string) => {
    if (onNavigate) onNavigate(path);
    else navigate(path);
    setIsOpen(false);
  };

  const handleMessageClick = (messageId: string) => {
    if (onMarkAsRead) onMarkAsRead(messageId);
    handleNavigation('/communication-center');
  };

  const getTimeAgo = (timestamp: string) => {
    const now = new Date();
    const messageTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now.getTime() - messageTime.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Chiar acum';
    if (diffInMinutes < 60) return `${diffInMinutes} min în urmă`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} h în urmă`;
    return `${Math.floor(diffInMinutes / 1440)} zile în urmă`;
  };

  const getPriorityColor = (priority?: string) => {
    switch (priority) {
      case 'high':
        return 'text-error';
      case 'low':
        return 'text-muted-foreground';
      default:
        return 'text-foreground';
    }
  };

  const recentMessages = messages.slice(0, 5);

  return (
    <div className="relative notification-container">
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="relative p-2 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150"
        aria-label={`Notificări${unreadCount > 0 ? ` (${unreadCount} necitite)` : ''}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Icon name="Bell" size={20} />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 flex items-center justify-center w-5 h-5 text-xs font-medium text-white bg-error rounded-full animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="fixed left-1/2 top-16 -translate-x-1/2 sm:absolute sm:left-auto sm:top-auto sm:right-0 sm:translate-x-0 mt-2 w-[min(96vw,24rem)] sm:w-96 bg-popover border border-border rounded-md shadow-lg z-[1010] animate-scale-in max-h-[70vh] overflow-y-auto">
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Notificări</h3>
              {unreadCount > 0 && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  {unreadCount} mesaj{unreadCount !== 1 ? 'e' : ''} necitit{unreadCount !== 1 ? 'e' : ''}
                </p>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleNavigation('/communication-center')}
              iconName="ExternalLink"
              iconSize={14}
            >
              Vezi toate
            </Button>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {recentMessages.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 px-4">
                <Icon name="Inbox" size={48} className="text-muted-foreground mb-3" />
                <p className="text-sm text-muted-foreground text-center">Nicio notificare nouă</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {recentMessages.map((message) => (
                  <button
                    key={message.id}
                    type="button"
                    onClick={() => handleMessageClick(message.id)}
                    className={`w-full text-left p-4 transition-colors duration-150 hover:bg-muted ${
                      !message.isRead ? 'bg-muted/50' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-xs font-medium">
                          {message.sender.charAt(0).toUpperCase()}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium truncate ${getPriorityColor(message.priority)}`}>
                              {message.sender}
                            </p>
                            <p className="text-xs text-muted-foreground capitalize">{message.senderRole}</p>
                          </div>
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {getTimeAgo(message.timestamp)}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-foreground mb-1 truncate">{message.subject}</p>
                        <p className="text-xs text-muted-foreground line-clamp-2">{message.preview}</p>
                        {!message.isRead && (
                          <div className="flex items-center gap-1 mt-2">
                            <div className="w-2 h-2 rounded-full bg-primary" />
                            <span className="text-xs font-medium text-primary">Necitit</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {recentMessages.length > 0 && (
            <div className="p-3 border-t border-border">
              <Button
                variant="outline"
                size="sm"
                fullWidth
                onClick={() => handleNavigation('/communication-center')}
                iconName="MessageSquare"
                iconPosition="left"
              >
                Deschide centrul de comunicare
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CommunicationNotifier;

